import { Controller } from "@hotwired/stimulus";
import SlimSelect from "slim-select";

const LOADER_ANIMATION_MINIMUM_DURATION_MS = 500;
const LOADER_ANIMATION_TIMEOUT_MS = 45000;

// This variable is used to store the current request that is being processed
// and eventual streams that would received from the websocket while the
// request is being processed. Rendering the streams directly could be a
// problem as their content could be overwritten by the new form after the
// request is done.
// There can be only one request at a time.
const STATE = {
  animationEndPromise: null,
  turboStreamTypesReceived: new Set(),
  turboStreamTypesToReceive: new Set(),
};

export default class extends Controller {
  static targets = [
    "select",
    "searchableSelect",
    "evaluationDefinitionPackageSection",
    "jobRequirementsFieldset",
    "descriptionField",
    "evaluationDefinitionPackageSectionAnimationContainer",
  ];

  static classes = ["hidden"];

  static values = {
    generateProfileSubmitType: String,
    stayOnCurrentStepSubmitTypes: Array,
    submitTypeKey: String,
  };

  DEFAULT_PROFILE_INPUT_TYPE = "job_description";

  TURBO_STREAM_TYPES_TO_RECEIVE_BY_PROFILE_INPUT = {
    job_description: new Set([
      "generate-profile",
      "profile-preview",
      "profile-field",
    ]),
    validated_role: new Set(["generate-profile"]),
  };

  initialize() {
    this.selects = [];

    this.handleBeforeStreamRender = this.beforeStreamRender.bind(this);
  }

  connect() {
    document.addEventListener(
      "turbo:before-stream-render",
      this.handleBeforeStreamRender,
    );
  }

  /**
   * Sets up and a SlimSelect from a target with optional options.
   * If the first option is empty, it is considered a placeholder.
   */
  setupSlimSelect(target, options = {}) {
    let allowDeselect = false;
    const firstChild = target.querySelector("option:first-child");
    if (firstChild.value === "") {
      firstChild.dataset.placeholder = "true";
      allowDeselect = true;
    }

    // Prevents the select from being translated by Google Translate.
    // On this page, when the select gets translated, the SlimSelect library
    // will change the DOM for it which will trigger a new translation by
    // Google Translate, which will change the DOM again, and so on.
    // Checking the value of the select in the submit method is not enough to
    // get the translation to work...
    target.classList.add("notranslate");

    const select = new SlimSelect({
      select: target,
      settings: { allowDeselect, ...options },
    });
    this.selects.push(select);
  }

  selectTargetConnected(target) {
    this.setupSlimSelect(target, { showSearch: false });
  }

  searchableSelectTargetConnected(target) {
    this.setupSlimSelect(target, { showSearch: true });
  }

  /**
   * Scrolls to the `profileSection` target when a profile is generated.
   */
  scrollToEvaluationDefinitionPackageSection() {
    if (this.hasEvaluationDefinitionPackageSectionTarget) {
      // The set timeout is needed to ensure the scroll is done after the
      // height of the section is adjusted by CSS.
      setTimeout(() => {
        this.evaluationDefinitionPackageSectionTarget.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 0);
    }
  }

  // eslint-disable-next-line class-methods-use-this
  getFormSubmissionProfileInputType(formSubmission) {
    const { entries } = formSubmission.fetchRequest;
    const profileInput = entries.find(
      ([name]) => name === "job-requirements-profile_input",
    );
    return profileInput ? profileInput[1] : this.DEFAULT_PROFILE_INPUT_TYPE;
  }

  startGenerateProfileAnimation(event) {
    const { detail: { formSubmission = {} } = {} } = event;

    const profileInputType =
      this.getFormSubmissionProfileInputType(formSubmission);

    // Checks that the description field is not empty (which is still valid)
    // before starting the animation.
    if (
      profileInputType === "job_description" &&
      this.descriptionFieldTarget.value.trim() === ""
    ) {
      return;
    }

    STATE.currentFormSubmission = formSubmission;
    STATE.turboStreamTypesReceived = new Set();

    STATE.turboStreamTypesToReceive =
      this.TURBO_STREAM_TYPES_TO_RECEIVE_BY_PROFILE_INPUT[profileInputType];

    // Creates a new promise that resolves once all the turbo streams have been
    // received.
    let animationEndCallback;

    STATE.animationEndPromise = new Promise((resolve) => {
      // Creates a promise that resolves once the stream for the profile
      // generation has been rendered. That's the trigger to render any other
      // streams that would have been received while the animation was running
      // and after the stream for the profile generation has been received.
      // This way we can ensure the rendering order of the different streams.
      animationEndCallback = () => {
        // Triggers the animation end event to render the stream.
        this.evaluationDefinitionPackageSectionAnimationContainerTarget.classList.add(
          this.hiddenClass,
        );
        // Waits for the CSS animation to finish before rendering the stream.
        setTimeout(() => {
          resolve();
        }, LOADER_ANIMATION_MINIMUM_DURATION_MS);
      };

      // Resolves the promise once the animation for the profile generation
      // has ended or after 30 seconds if the event is not fired.
      this.element.addEventListener(
        "all-turbo-streams-received",
        animationEndCallback,
        { once: true },
      );
      setTimeout(() => {
        resolve();
      }, LOADER_ANIMATION_TIMEOUT_MS);
    }).then(() => {
      // Resets the state variables once the animation is done and remove the
      // event listener.
      this.element.removeEventListener(
        "all-turbo-streams-received",
        animationEndCallback,
      );
    });

    this.scrollToEvaluationDefinitionPackageSection();
    this.jobRequirementsFieldsetTarget.disabled = true;
    this.evaluationDefinitionPackageSectionAnimationContainerTarget.classList.remove(
      this.hiddenClass,
    );
  }

  /**
   * This method filters the submit events from generate profile and dispatches
   * the `main-submit-start` event that then can be listened to by other
   * controllers interested in the form submission such as the
   * `LoaderOverlay` controller.
   *
   * profile request.
   *
   * @param {Event} event
   */
  submitStart(event) {
    const { detail: { formSubmission = {} } = {} } = event;
    const { submitter = {} } = formSubmission;
    const { name: submitterName, value: submitterValue } = submitter;

    // Skips dispatch if this is a `_submit_type` submit, and its value starts
    // with one of the values in `stayOnCurrentStepSubmitTypesValue`.
    const skipDispatch =
      submitterName === this.submitTypeKeyValue &&
      this.stayOnCurrentStepSubmitTypesValue.some((value) =>
        submitterValue.startsWith(value),
      );

    if (skipDispatch) {
      return;
    }

    if (
      submitterName === this.submitTypeKeyValue &&
      submitterValue === this.generateProfileSubmitTypeValue
    ) {
      this.startGenerateProfileAnimation(event);
      return;
    }

    this.dispatch("main-submit-start", event);
  }

  /**
   * This method filters the submit events from generate profile and dispatches
   * the main-submit-end event that then can be listened to by other
   * controllers interested in the form submission such as the `LoaderOverlay`
   * controller.
   *
   * @param {Event} event
   */
  submitEnd(event) {
    const { detail: { formSubmission = {}, success } = {} } = event;
    const { submitter = {} } = formSubmission;
    const { name: submitterName, value: submitterValue } = submitter;

    // Skips dispatch if this is a `_submit_type` submit, and its value starts
    // with one of the values in `stayOnCurrentStepSubmitTypesValue`.
    const skipDispatch =
      submitterName === this.submitTypeKeyValue &&
      this.stayOnCurrentStepSubmitTypesValue.some((value) =>
        submitterValue.startsWith(value),
      );

    if (skipDispatch) {
      return;
    }

    // Lets the loading animation go on failed generate profile requests, as
    // there are no other streams coming besides the main one.
    if (
      submitterValue === this.generateProfileSubmitTypeValue &&
      success === false
    ) {
      this.element.dispatchEvent(new Event("all-turbo-streams-received"));
    }

    this.dispatch("main-submit-end", event);

    if (success) {
      this.dispatch("form-submitted-with-success", event);
    }
  }

  // eslint-disable-next-line class-methods-use-this
  async beforeStreamRender(event) {
    const fallbackToDefaultActions = event.detail.render;

    const {
      detail: { newStream },
    } = event;
    const { type } = newStream.dataset;

    // Pause the stream rendering until the animation for the profile
    // generation has ended.
    // eslint-disable-next-line no-param-reassign
    event.detail.render = async (streamElement) => {
      await STATE.animationEndPromise;
      fallbackToDefaultActions(streamElement);
    };

    STATE.turboStreamTypesReceived.add(type);

    // Checks if all the streams have been received and dispatches the related
    // event to end the animation and then render the streams.
    if (
      STATE.turboStreamTypesReceived.size !==
      STATE.turboStreamTypesToReceive.size
    ) {
      return;
    }

    let allStreamsReceived = false;
    // eslint-disable-next-line no-restricted-syntax
    for (const streamType of STATE.turboStreamTypesToReceive) {
      if (!STATE.turboStreamTypesReceived.has(streamType)) {
        allStreamsReceived = false;
        break;
      }
      allStreamsReceived = true;
    }

    if (allStreamsReceived === false) {
      return;
    }

    this.element.dispatchEvent(new Event("all-turbo-streams-received"));
  }

  /**
   * Intercepts redirect responses and changes the URL to match the new one.
   *
   * This does not hand control over to react-router as that causes double GETs
   * in the react web app. Changing the URL on successful submit via
   * react-router would cause another GET from <turbo-frame />'s src change,
   * and Turbo's fetch does `redirect="follow"` which cannot be stopped with
   * `preventDefault`.
   */
  // eslint-disable-next-line class-methods-use-this
  updateURLFromRedirectResponse(event) {
    const {
      detail: { fetchResponse },
    } = event;
    const {
      redirected,
      response: { url },
    } = fetchResponse;

    if (redirected) {
      const { pathname, search } = new URL(url);
      const to = `${pathname}${search}`;
      window.history.pushState({}, "", to);
    }
  }

  disconnect() {
    this.selects.forEach((select) => select.destroy());
    this.selects = [];
    document.removeEventListener(
      "turbo:before-stream-render",
      this.handleBeforeStreamRender,
    );
  }
}
